import {
  BriefcaseBusiness,
  CalendarClock,
  Clock,
  ClockArrowDown,
  ClockArrowUp,
} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetMyWorkTimeStatusQuery,
  useStartMyWorkTimeShiftMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import {useMyWorkTimeStatus} from '../hooks/useMyWorkTimeStatus';
import LiveDuration from './LiveDuration';

const WorkTimeMenuSection = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isWorking, activeSession, isFetching} = useMyWorkTimeStatus();
  const startWork = useStartMyWorkTimeShiftMutation(graphqlClient);
  const isActionPending = startWork.isPending || isFetching;

  const handleStartWork = async () => {
    await startWork.mutateAsync({});
    await queryClient.invalidateQueries({
      queryKey: useGetMyWorkTimeStatusQuery.getKey(),
    });
    navigate('/');
  };

  const handleFinishWork = () => {
    searchParams.set('finishWork', 'true');
    setSearchParams(searchParams);
  };

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>
        {isWorking ? t('Work is started') : t('Work is not started')}
      </DropdownMenuLabel>
      {isWorking && (
        <div className="flex items-center gap-2 px-2 pb-1 text-muted-foreground">
          <Clock className="size-4 shrink-0" />
          <span className="font-mono text-sm tabular-nums">
            <LiveDuration startedAt={activeSession?.startedAt} />
          </span>
        </div>
      )}
      {!isWorking && (
        <div className="flex items-center gap-2 px-2 pb-1 text-sm text-muted-foreground">
          <BriefcaseBusiness className="size-4 shrink-0" />
          <span className="text-sm">{t('Not started')}</span>
        </div>
      )}
      <DropdownMenuSeparator />
      {isWorking ? (
        <DropdownMenuItem disabled={isActionPending} onClick={handleFinishWork}>
          <ClockArrowDown className="size-4" />
          {t('Finish work')}
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem disabled={isActionPending} onClick={handleStartWork}>
          <ClockArrowUp className="size-4" />
          {t('Start work')}
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild>
        <Link className="flex items-center gap-2" to="/work-time/history">
          <CalendarClock className="size-4" />
          {t('Check my work time')}
        </Link>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  );
};

export default WorkTimeMenuSection;
